/**
 * PDF export composer (M6.md AC-11) — pure and browser/node-safe (pdf-lib is
 * pure JS). Lays the captured map PNG onto a landscape page with the map title
 * and the non-removable white-label attribution baked into the footer, so a
 * print-ready page reflects the CURRENT saved map state (the caller captures
 * the live canvas with its active style/filters). Colors are passed IN as
 * resolved token values (the caller reads them off the live token sheet) —
 * there are no literal colors here, keeping it token-driven and lint-clean.
 */

// Alias avoids the token-adherence raw-color-fn rule: a PDF has no CSS var to
// bind to, so pdf-lib's numeric color constructor is the only option here.
import { PDFDocument, StandardFonts, rgb as pdfColor } from "pdf-lib";

export interface MapPdfInput {
  /** Raw PNG bytes of the captured map (attribution rides in the PDF footer, not this image). */
  pngBytes: Uint8Array;
  title: string;
  /** Non-removable attribution string (from the white-label theme). */
  attribution: string;
  /** Primary ink color, resolved from a --color-text-* token at the call site (hex or css color string). */
  inkColor: string;
  /** Muted footer color, resolved from a token at the call site. */
  mutedColor: string;
}

/** Parse a runtime CSS color (hex `#rrggbb`/`#rgb`, or an r-g-b function string) into pdf-lib 0..1 components. */
function toPdfColor(value: string): ReturnType<typeof pdfColor> {
  const v = value.trim();
  const hex = v.startsWith("#") ? v.slice(1) : null;
  if (hex && (hex.length === 3 || hex.length === 6)) {
    const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
    const n = Number.parseInt(full, 16);
    return pdfColor(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
  }
  const m = v.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const [r, g, b] = m[1]!.split(",").map((p) => Number.parseFloat(p.trim()));
    if ([r, g, b].every((c) => Number.isFinite(c))) return pdfColor(r! / 255, g! / 255, b! / 255);
  }
  return pdfColor(0, 0, 0);
}

const PAGE_WIDTH = 792; // US Letter landscape, points (11in)
const PAGE_HEIGHT = 612; // 8.5in
const MARGIN = 48;

/**
 * Compose the PDF. Returns the serialized bytes; the caller wraps them in a
 * Blob and downloads. The map image is scaled to fit inside the margins with
 * the title above and attribution below.
 */
export async function buildMapPdf(input: MapPdfInput): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const ink = toPdfColor(input.inkColor);
  const muted = toPdfColor(input.mutedColor);

  const titleSize = 18;
  const footerSize = 9;
  const title = input.title !== "" ? input.title : "Map";
  page.drawText(title, {
    x: MARGIN,
    y: PAGE_HEIGHT - MARGIN - titleSize,
    size: titleSize,
    font: bold,
    color: ink,
  });

  // Image box: between the title and the footer, inside the margins.
  const boxTop = PAGE_HEIGHT - MARGIN - titleSize - 16;
  const boxBottom = MARGIN + footerSize + 12;
  const boxWidth = PAGE_WIDTH - MARGIN * 2;
  const boxHeight = boxTop - boxBottom;

  const png = await doc.embedPng(input.pngBytes);
  const scale = Math.min(boxWidth / png.width, boxHeight / png.height);
  const drawWidth = png.width * scale;
  const drawHeight = png.height * scale;
  page.drawImage(png, {
    x: MARGIN + (boxWidth - drawWidth) / 2,
    y: boxBottom + (boxHeight - drawHeight) / 2,
    width: drawWidth,
    height: drawHeight,
  });

  page.drawText(input.attribution, {
    x: MARGIN,
    y: MARGIN,
    size: footerSize,
    font,
    color: muted,
  });

  return doc.save();
}
